import { useMemo } from 'react'

import { getNativeCurrency } from '@/config/native'
import { useAllTransactions } from '@/domain/transactions'
import type { TransactionDetails } from '@/domain/transactions'
import { useSwapCatalogTokens } from '@/features/swap/useSwapCatalogTokens'
import { useAppWeb3 } from '@/hooks'
import { useAllTokens } from '@/hooks/Tokens'
import type { Currency, CurrencyAmount } from '@/sdk'
import { ChainId, JSBI } from '@/sdk'
import { useTokenBalances, useCurrencyBalance } from '@/state/wallet/hooks'

import { mergePortfolioTrackedTokens } from './mergePortfolioTrackedTokens'

export type PortfolioAsset = {
  currency: Currency
  balance: CurrencyAmount
}

export { mergePortfolioTrackedTokens } from './mergePortfolioTrackedTokens'

export function usePortfolioAssets(): {
  account: string | undefined
  assets: PortfolioAsset[]
  nativeBalance: CurrencyAmount | undefined
} {
  const { account, chainId } = useAppWeb3()
  const allTokens = useAllTokens()
  const catalogTokens = useSwapCatalogTokens(chainId ?? ChainId.INFINITE_CREATIVE)
  const trackedTokens = useMemo(
    () => (chainId != null ? mergePortfolioTrackedTokens(chainId, allTokens, catalogTokens) : []),
    [allTokens, catalogTokens, chainId],
  )
  const tokenBalances = useTokenBalances(account ?? undefined, trackedTokens)
  const nativeCurrency = chainId != null ? getNativeCurrency(chainId) : undefined
  const nativeBalance = useCurrencyBalance(account ?? undefined, nativeCurrency)

  const assets = useMemo(() => {
    const items: PortfolioAsset[] = []

    if (nativeBalance && nativeBalance.greaterThan(JSBI.BigInt(0))) {
      items.push({ currency: nativeBalance.currency, balance: nativeBalance })
    }

    for (const token of trackedTokens) {
      const balance = tokenBalances[token.address]
      if (balance && balance.greaterThan(JSBI.BigInt(0))) {
        items.push({ currency: token, balance })
      }
    }

    return items.sort((a, b) =>
      (a.currency.symbol ?? '').localeCompare(b.currency.symbol ?? '', undefined, {
        sensitivity: 'base',
      }),
    )
  }, [nativeBalance, tokenBalances, trackedTokens])

  return { account, assets, nativeBalance }
}

export function usePortfolioMovements(): TransactionDetails[] {
  const { account } = useAppWeb3()
  const allTransactions = useAllTransactions()

  return useMemo(() => {
    if (!account) return []
    return Object.values(allTransactions)
      .filter((tx) => tx.from.toLowerCase() === account.toLowerCase())
      .sort((a, b) => (b.addedTime ?? 0) - (a.addedTime ?? 0))
  }, [account, allTransactions])
}
